import { createFileRoute, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { UpsertOrganizationForm } from '@/src/components/manage/mutate/upsertOrganization'
import { OrgUrlAllowlistCard } from '@/src/components/manage/organization/settings/OrganizationWorkflowSecurityTab'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { listOrganizations } from '@/src/api/organization'
import { listUsers } from '@/src/api/user'
import { Spinner } from '@/src/components/ui/spinner'

export const Route = createFileRoute('/_manage/manage/admin/organizations/$id')({
  component: OrganizationEditPage,
})

function OrganizationEditPage() {
  const { id: orgId } = useParams({ from: '/_manage/manage/admin/organizations/$id' })
  const isNew = orgId === 'new'

  const { data: organizations, isLoading, error } = useQuery({
    queryKey: ['organization', 'list'],
    queryFn: () => listOrganizations(),
    enabled: !isNew,
  })

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => listUsers(),
  })

  const org = isNew ? null : organizations?.find((o: any) => o.id === orgId)

  if (isLoading || usersLoading) {
    return (
      <div>
        <AdminToolbar>
          <AdminToolbar.Left>
            <AdminToolbar.Title>{isNew ? 'Create Organization' : 'Edit Organization'}</AdminToolbar.Title>
          </AdminToolbar.Left>
        </AdminToolbar>
        <div className="max-w-4xl mx-auto p-6">
          <Spinner size={48} />
        </div>
      </div>
    )
  }

  if (!isNew && !org) {
    return (
      <div>
        <AdminToolbar>
          <AdminToolbar.Left>
            <AdminToolbar.Title>Edit Organization</AdminToolbar.Title>
          </AdminToolbar.Left>
        </AdminToolbar>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-destructive">Organization not found.</div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.Title>{isNew ? 'Create Organization' : 'Edit Organization'}</AdminToolbar.Title>
        </AdminToolbar.Left>
      </AdminToolbar>
      {!isNew && error ? (
        <div className="text-destructive mb-4 px-6">
          {error instanceof Error ? error.message : 'Failed to load organization.'}
        </div>
      ) : (
        <div className="space-y-6">
          <UpsertOrganizationForm
            id={org?.id}
            isNew={isNew}
            organization={
              isNew
                ? { name: '', description: '' }
                : {
                    id: org!.id,
                    name: org!.name,
                    description: org!.description ?? undefined,
                    domain: org!.domain ?? undefined,
                    logo: org!.logo ?? undefined,
                    autoJoin: (org as any)?.autoJoin ?? false,
                    ownerId: (org as any)?.ownerId ?? undefined,
                  }
            }
            availableUsers={users || []}
            callbackUrl="/manage/admin/organizations"
          />
          {!isNew && org && (
            <div className="max-w-4xl mx-auto px-6 pb-6">
              <OrgUrlAllowlistCard orgId={org.id} isSystemAdmin />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
